import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../../../Utils/api';
import useAsync from '../../../Hooks/useAsync';
import { weekBoundaries } from '../../../Utils/dates/localDay';

/**
 * One ranged fetch (four local weeks) shared by Weekly load, Past workouts and Training rhythm,
 * plus markCompleted: optimistic row update, one API call per row, then a refetch so every card
 * recomputes from the server's state.
 */
const useAthleteWorkouts = (athleteId, now) => {
    const range = useMemo(() => {
        const b = weekBoundaries(now, 4);
        return { from: b[0], to: b[4] };
    }, [now]);

    const { status, data, error, reload } = useAsync(
        () => API.getAthleteWorkoutSummary(athleteId, range).then(res => res.data),
        [athleteId, range]
    );
    const [overrides, setOverrides] = useState({});
    const [pendingIds, setPendingIds] = useState([]);
    const inFlight = useRef(new Set()); // synchronous guard: state would be stale inside one tick

    const rows = useMemo(
        () => (data ? data.map(r => (overrides[r.id] ? { ...r, status: overrides[r.id] } : r)) : data),
        [data, overrides]
    );

    const finish = useCallback(id => {
        inFlight.current.delete(id);
        setPendingIds(ids => ids.filter(x => x !== id));
        setOverrides(o => {
            const { [id]: _removed, ...rest } = o;
            return rest;
        });
    }, []);

    const markCompleted = useCallback(
        async id => {
            if (inFlight.current.has(id)) return;
            inFlight.current.add(id);
            setPendingIds(ids => [...ids, id]);
            setOverrides(o => ({ ...o, [id]: 'Completed' }));
            try {
                await API.completeWorkout(id);
                reload();
            } catch (err) {
                if (err?.response?.status === 409) {
                    toast.info('This session was already updated.');
                    reload();
                } else {
                    toast.error("Couldn't mark the session completed. Try again.");
                }
            } finally {
                finish(id);
            }
        },
        [reload, finish]
    );

    return { status, data: rows, error, reload, markCompleted, pendingIds };
};

export default useAthleteWorkouts;
